import { Dispatch, SetStateAction, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Flex, Space, Tag, Tooltip, Typography } from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import Table, { ColumnsType } from 'antd/es/table'
import { path } from 'src/libs'
import { getFinsystemNumberFormat } from 'src/libs/utils'
import { useDebtList } from 'src/pages/finSystem/debt/hook'
import { Debt, ListRequest } from 'src/pages/finSystem/debt/types'
import { get_sorter } from 'src/pages/finSystem/debt/utils'
import moment from 'moment'
import { Guard } from 'src/features/session/guard'
import { PERMISSIONS } from 'src/features/session/constants'

type Props = {
  setFilters: (values: ListRequest) => void
  sorterField?: string
  setDebtEdit: Dispatch<SetStateAction<Debt | null>>
  setDebtDelete: Dispatch<SetStateAction<Debt | null>>
}

export const DebtListTable = ({
  setFilters,
  sorterField,
  setDebtEdit,
  setDebtDelete,
}: Props) => {
  const { debts, totalItems, totalsDebts, loading } = useDebtList()
  const { balanceDueUsd, recoveredUsd } = totalsDebts

  const { Text } = Typography

  const columns: ColumnsType<Debt> = [
    {
      title: 'ID CRM',
      dataIndex: 'id',
    },
    {
      title: 'Wallet',
      render: (record: Debt) => (
        <Link
          to={path.finSystem.wallets.details(String(record.walletId))}
          style={{ textDecoration: 'none' }}
        >
          {record.walletName}
        </Link>
      ),
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      render: (id) => (
        <Link to={path.clients.details(id)} style={{ textDecoration: 'none' }}>
          {id}
        </Link>
      ),
    },
    {
      title: 'Date Time',
      dataIndex: 'dateTime',
      width: 160,
      sorter: true,
      defaultSortOrder: (() => {
        const sorter = get_sorter(sorterField)
        if (!sorter) return undefined
        const { field, order } = sorter
        if (field !== 'dateTime') return undefined
        return order
      })(),
      render: (d) => (
        <>
          {moment.parseZone(d).format('YYYY-MM-DD')}{' '}
          <Text type="secondary">{moment.parseZone(d).format('HH:mm')}</Text>
        </>
      ),
    },
    {
      title: (
        <Text>
          Initial Debt{' '}
          <Tooltip title="The original total debt amount.">
            <QuestionCircleOutlined
              style={{ cursor: 'help', color: 'var(--crm-text-secondary)' }}
            />
          </Tooltip>
        </Text>
      ),
      dataIndex: 'debtAmount',
      render: (it) => <>{getFinsystemNumberFormat(it, 2)}</>,
    },
    {
      title: (
        <Text>
          Recovered{' '}
          <Tooltip title="The amount that has already been recovered from the customer">
            <QuestionCircleOutlined
              style={{ cursor: 'help', color: 'var(--crm-text-secondary)' }}
            />
          </Tooltip>
        </Text>
      ),
      dataIndex: 'recoveredAmount',
      render: (it) => <>{getFinsystemNumberFormat(it, 2)}</>,
    },
    {
      title: 'Balance Due, local',
      dataIndex: 'balanceDue',
      render: (it) => <>{getFinsystemNumberFormat(it, 2)}</>,
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      render: (it) => (
        <Text type="secondary" style={{ fontSize: 16 }}>
          {it.toUpperCase()}
        </Text>
      ),
    },
    {
      title: 'Balance Due, USD',
      dataIndex: 'balanceDueUsd',
      defaultSortOrder: (() => {
        const sorter = get_sorter(sorterField)
        if (!sorter) return undefined
        const { field, order } = sorter
        if (field !== 'balanceDueUsd') return undefined
        return order
      })(),
      sorter: true,
      render: (it) => <>{getFinsystemNumberFormat(it, 2)}</>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 90,
      render: (v: 'Active' | 'Closed') =>
        v === 'Active' ? (
          <Tag color="success" style={{ fontSize: 10, fontWeight: 700 }}>
            Active
          </Tag>
        ) : (
          <Tag color="red" style={{ fontSize: 10, fontWeight: 700 }}>
            Closed
          </Tag>
        ),
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      onHeaderCell: () => ({
        className: 'resize_column',
      }),
      key: 'comment',
    },
    {
      title: 'Actions',
      align: 'center',
      fixed: 'right',
      width: 100,
      render: (item: Debt) => {
        return (
          <Guard
            permission={PERMISSIONS.EDIT_FINSYSTEM_DEBT_TRANSACTION}
            mode="permission"
          >
            <Space>
              <Tooltip title="Edit debt details">
                <Button
                  type="text"
                  icon={
                    <EditOutlined
                      style={{ color: 'var(--color-primary)', fontSize: 16 }}
                    />
                  }
                  onClick={() =>
                    setDebtEdit({ ...item, wallet: item.walletId })
                  }
                />
              </Tooltip>
              <Tooltip title="Delete this debt record">
                <Button
                  type="text"
                  danger
                  style={{ fontSize: 16 }}
                  icon={<DeleteOutlined />}
                  onClick={() => setDebtDelete(item)}
                />
              </Tooltip>
            </Space>
          </Guard>
        )
      },
    },
  ]
  return (
    <>
      <Flex
        className="even-row"
        style={{ padding: 16 }}
        gap={24}
        justify="flex-end"
      >
        <div>
          <Text style={{ fontSize: 20 }}>
            Total dept: {getFinsystemNumberFormat(balanceDueUsd, 2)}{' '}
            <Text style={{ fontSize: 20 }} type="secondary">
              USD
            </Text>
          </Text>
        </div>
        <div>
          <Text style={{ fontSize: 20 }}>
            Total Recovered: {getFinsystemNumberFormat(recoveredUsd, 2)}{' '}
            <Text style={{ fontSize: 20 }} type="secondary">
              USD
            </Text>
          </Text>
        </div>
      </Flex>
      <br />
      <Table
        dataSource={debts}
        className="overflow-auto"
        loading={loading}
        rowKey="id"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalItems,
          position: ['bottomCenter'],
        }}
        rowClassName={(_, index) => `${index % 2 ? 'even-row' : 'odd-row'}`}
        onChange={(pagination, _, sorter: any) => {
          setFilters({ page: (pagination.current ?? 1) as any })
          setFilters({ pageSize: (pagination.pageSize ?? 10) as any })

          const sorterField =
            sorter.order === 'descend' ? `-${sorter.field}` : sorter.field

          setFilters({ sorterField })
        }}
      />
    </>
  )
}
